import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Drawer,
    Box,
    Card,
    CardHeader,
    CardContent,
    Divider,
    Stack,
    Typography,
    IconButton,
    Tooltip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Pagination,
} from '@mui/material';
import { DataGrid, GridToolbarContainer } from '@mui/x-data-grid';
import CloseIcon from '@mui/icons-material/Close';
import LinearProgress from '@mui/material/LinearProgress';
import NorowsData from '../../General/NorowsData';
import WidthMaximize from '../../General/WidthMaximize';
import { fetchGroups } from './shipFromSavingsSlice';

const ACCENT = '#146C43';

function formatMoney(value) {
    if (value === undefined || value === null) return '—';
    return `$${Number(value).toFixed(2)}`;
}

const outcomeChipStyle = (outcome) => {
    const isMove = outcome === 'Would move';
    return {
        backgroundColor: isMove ? '#E9F3EC' : '#F1F5F9',
        color: isMove ? ACCENT : '#64748B',
        fontSize: '12px',
        fontWeight: 700,
        padding: '4px 12px',
        borderRadius: '999px',
        display: 'inline-block',
    };
};

const FlaggedOrdersDrawer = ({ visible, storeId, onClose }) => {
    const dispatch = useDispatch();
    const groups = useSelector((state) => state.shipFromSavings && state.shipFromSavings.groups);
    const [drawerWidth, setDrawerWidth] = useState('80%');
    const [outcome, setOutcome] = useState('would_move');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);

    useEffect(() => {
        if (visible) {
            dispatch(fetchGroups({ storeId, outcome, page, pageSize }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, storeId, outcome, page, pageSize]);

    const handleClose = (event, reason) => {
        if (reason !== 'backdropClick') {
            onClose();
        }
    };

    const onChangeOutcome = (event) => {
        setOutcome(event.target.value);
        setPage(1);
    };

    const onChangePerPage = (event) => {
        setPageSize(Number(event.target.value));
        setPage(1);
    };

    const onChangePagination = (event, value) => {
        setPage(value);
    };

    const columns = [
        {
            field: 'order_id',
            headerName: 'Order',
            headerClassName: 'super-app-theme--header',
            sortable: false,
            flex: 1,
            renderCell: (params) => (
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#0F172A', fontSize: '13.5px', fontFamily: 'monospace', display: 'inline-flex', alignItems: 'center', height: '100%' }}>
                    {params.row.order_id}
                </Typography>
            ),
        },
        {
            field: 'origin_location_name',
            headerName: 'Assigned location',
            headerClassName: 'super-app-theme--header',
            sortable: false,
            flex: 1.4,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
                    <Typography variant="body2" sx={{ color: '#0F172A', fontWeight: 600, fontSize: '13.5px' }}>
                        {params.row.origin_location_name || '—'}
                    </Typography>
                    {params.row.origin_rate != null && (
                        <Typography variant="caption" sx={{ color: '#64748B', fontFamily: 'monospace', fontSize: '12px' }}>
                            ${params.row.origin_rate}
                        </Typography>
                    )}
                </Box>
            ),
        },
        {
            field: 'cheapest_location_name',
            headerName: 'Cheapest other location',
            headerClassName: 'super-app-theme--header',
            sortable: false,
            flex: 1.4,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
                    <Typography variant="body2" sx={{ color: '#0F172A', fontWeight: 600, fontSize: '13.5px' }}>
                        {params.row.cheapest_location_name || '—'}
                    </Typography>
                    {params.row.cheapest_rate != null && (
                        <Typography variant="caption" sx={{ color: ACCENT, fontFamily: 'monospace', fontWeight: 700, fontSize: '12px' }}>
                            ${params.row.cheapest_rate}
                        </Typography>
                    )}
                </Box>
            ),
        },
        {
            field: 'savings',
            headerName: 'Saving',
            headerClassName: 'super-app-theme--header',
            sortable: false,
            flex: 0.9,
            renderCell: (params) => (
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 700,
                        fontFamily: 'monospace',
                        fontSize: '13.5px',
                        color: params.row.savings > 0 ? ACCENT : '#94A3B8',
                        display: 'inline-flex',
                        alignItems: 'center',
                        height: '100%',
                    }}
                >
                    {params.row.savings > 0 ? formatMoney(params.row.savings) : '—'}
                </Typography>
            ),
        }
    ];

    function CustomToolbar() {
        return (
            <GridToolbarContainer sx={{ p: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #E2E8F0', backgroundColor: '#F8FAFC', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    {/* <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel id="outcome-filter-label">Outcome</InputLabel>
                        <Select
                            labelId="outcome-filter-label"
                            label="Outcome"
                            value={outcome}
                            onChange={onChangeOutcome}
                            sx={{ backgroundColor: '#FFFFFF', borderRadius: '8px', fontSize: '13px' }}
                        >
                            <MenuItem value="would_move">Would move</MenuItem>
                            <MenuItem value="skipped">Skipped</MenuItem>
                            <MenuItem value="">All</MenuItem>
                        </Select>
                    </FormControl> */}

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#64748B', fontSize: '13px' }}>Show</Typography>
                        <select
                            value={pageSize}
                            onChange={onChangePerPage}
                            style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid #E2E8F0', color: '#0F172A', fontWeight: 600, fontSize: '13px', outline: 'none', backgroundColor: '#FFFFFF', cursor: 'pointer' }}
                        >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#64748B', fontSize: '13px' }}>entries</Typography>
                    </Box>
                </Box>

                <Typography variant="body2" sx={{ fontWeight: 600, color: '#64748B', fontSize: '13px' }}>
                    Showing {(groups && groups.data && groups.data.length) || 0} of {(groups && groups.meta && groups.meta.total) || 0} records
                </Typography>
            </GridToolbarContainer>
        );
    }

    function CustomPagination() {
        return (
            <Box sx={{ p: 2.5, display: 'flex', justifyContent: 'center', borderTop: '1px solid #E2E8F0', backgroundColor: '#FFFFFF' }}>
                <Pagination
                    color="primary"
                    shape="rounded"
                    size="small"
                    page={(groups && groups.meta && groups.meta.page) || 1}
                    count={(groups && groups.meta && groups.meta.total_pages) || 0}
                    onChange={onChangePagination}
                    sx={{
                        '& .MuiPaginationItem-root': { fontWeight: 700, borderRadius: '8px' },
                        '& .Mui-selected': { boxShadow: '0 2px 4px rgba(25, 118, 210, 0.15)' },
                    }}
                />
            </Box>
        );
    }

    return (
        <Drawer
            anchor="right"
            open={visible}
            onClose={handleClose}
            PaperProps={{ sx: { width: drawerWidth, bgcolor: '#FAFAFA' } }}
        >
            <Box sx={{ p: 4, height: '100%', boxSizing: 'border-box', overflowY: 'auto' }}>
                <Stack spacing={3}>
                    <Card sx={{ backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.03)', overflow: 'hidden' }}>
                        <CardHeader
                            title={
                                <Typography variant="h6" component="h1" sx={{ fontWeight: 800, color: '#0F172A', fontSize: '16px', m: 0 }}>
                                    Flagged orders
                                </Typography>
                            }
                            action={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <WidthMaximize defaultWidth="80%" onToggle={(newWidth) => setDrawerWidth(newWidth)} />
                                    <Tooltip title="Close Panel" placement="top">
                                        <IconButton
                                            aria-label="close"
                                            onClick={onClose}
                                            sx={{ color: '#64748B', borderRadius: '8px', border: '1px solid #E2E8F0', p: 0.75, transition: 'all 0.15s ease-in-out', '&:hover': { backgroundColor: '#F1F5F9', color: '#1E293B', borderColor: '#CBD5E1' } }}
                                        >
                                            <CloseIcon sx={{ fontSize: '18px' }} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            }
                            sx={{ p: 2.5, backgroundColor: '#FFFFFF' }}
                        />
                        <Divider sx={{ borderColor: '#E2E8F0' }} />
                    </Card>

                    <Card sx={{ border: '1px solid #E2E8F0', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.03)', backgroundColor: '#FFFFFF' }}>
                        <DataGrid
                            getRowId={(row) => `${row.order_id}-${row.origin_location_name}-${row.computed_at}`}
                            rows={(groups && groups.data) || []}
                            columns={columns}
                            autoHeight
                            disableColumnFilter
                            disableColumnMenu
                            disableRowSelectionOnClick
                            loading={!!(groups && groups.loading)}
                            sx={{
                                border: 'none',
                                '& .MuiDataGrid-columnHeader': { color: '#475569', fontWeight: 700, fontSize: '13px', '&:focus, &:focus-within': { outline: 'none' } },
                                '& .MuiDataGrid-row': { transition: 'background-color 0.15s ease-in-out', borderBottom: '1px solid #F1F5F9', alignItems: 'center', '&:hover': { backgroundColor: '#F8FAFC' } },
                                '& .MuiDataGrid-cell': { borderBottom: 'none', display: 'flex', alignItems: 'center', py: '12px', '&:focus, &:focus-within': { outline: 'none' } },
                                '& .MuiLinearProgress-root': { height: '3px', backgroundColor: '#E2E8F0', '& .MuiLinearProgress-bar': { backgroundColor: ACCENT } },
                            }}
                            slots={{
                                pagination: CustomPagination,
                                toolbar: CustomToolbar,
                                noRowsOverlay: NorowsData,
                                loadingOverlay: LinearProgress,
                            }}
                        />
                    </Card>
                </Stack>
            </Box>
        </Drawer>
    );
};

export default FlaggedOrdersDrawer;